"use client";

import { CallFileType, CallStatus, ProjectMemberRole } from "@prisma/client";
import { useEffect, useRef, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import Badge from "@/app/(components)/generics/Badge/Badge";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import IconDocument from "@/app/(components)/generics/Icons/IconDocument";
import IconHeadphones from "@/app/(components)/generics/Icons/IconHeadphones";
import IconVideo from "@/app/(components)/generics/Icons/IconVideo";
import ToggleInput from "@/app/(components)/generics/inputs/default/ToggleInput/ToggleInput";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import Pagination from "@/app/(components)/generics/Pagination/Pagination";
import CallTitle from "@/app/(components)/specifics/tools/CallAnalysis/components/CallTitle/CallTitle";
import PeriodFilter from "@/app/(components)/specifics/tools/CallAnalysis/components/PeriodFilter/PeriodFilter";
import StatusFilter from "@/app/(components)/specifics/tools/CallAnalysis/components/StatusFilter/StatusFilter";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useBrand,
  useCanEditContent,
  useCanManageCompany,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { setProjectCallAnalysisAutoRunRoles } from "@/app/actions/project/set-auto-run/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKeyCallAnalysis as TKey } from "@/app/translations/keys/TKeyCallAnalysis";
import { TKeyProjectMember } from "@/app/translations/keys/TKeyProjectMember";
import { useT } from "@/app/translations/useT";
import { CallFile } from "@/app/types/call/CallFile";
import { CallPeriod, CallPeriodRange } from "@/app/types/call/CallPeriod";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PaginationData } from "@/app/types/common/PaginationData";
import { ProjectMemberData } from "@/app/types/project/ProjectMember";
import { UFile } from "@/app/utils/UFile";
import { UTime } from "@/app/utils/UTime";
import { Limits } from "@/app/vars/Limits";
import CallDetailFooter from "../CallDetail/components/CallDetailFooter";
import CallRowMenu from "../CallRowMenu/CallRowMenu";
import CallStatusBadge from "../CallStatusBadge/CallStatusBadge";
import MemberFilter from "../MemberFilter/MemberFilter";
import MemberSelector from "../MemberSelector/MemberSelector";
import RunAnalysis, { RunAnalysisMode } from "../RunAnalysis/RunAnalysis";
import TypeFilter from "../TypeFilter/TypeFilter";
import TypeSelector from "../TypeSelector/TypeSelector";
import {
  CALLS_PLACEHOLDER_ROWS,
  CallColumn,
  getCallDisplayName,
  ITEMS_PER_PAGE,
  resolveCallFileType,
} from "./Calls.logic";
import styles from "./Calls.module.css";
import CallInsightToggle from "./components/CallInsightToggle/CallInsightToggle";

type Props = DefaultProps & {
  files: CallFile[];
  totalCount: number;
  totalSize: number;
  totalDuration: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  onFileRemove: (id: number) => void;
  onFileRename: (id: number, label: string) => void;
  onTypeChange: (id: number, type: string | null) => void;
  onStatusUpdate: (id: number, status: CallStatus) => void;
  onUploadClick?: () => void;
  members: ProjectMemberData[];
  onMemberChange: (id: number, memberId: number | null) => void;
  onAddMember: (callId: number) => void;
  memberFilter: number | null | undefined;
  onMemberFilterChange: (memberId: number | null | undefined) => void;
  onSelfAssign: (callId: number) => void;
  typeFilter: string | null | undefined;
  onTypeFilterChange: (type: string | null | undefined) => void;
  statusFilter: CallStatus | null | undefined;
  onStatusFilterChange: (status: CallStatus | null | undefined) => void;
  period: CallPeriod;
  customRange: CallPeriodRange;
  onPeriodChange: (period: CallPeriod, custom: CallPeriodRange) => void;
  projectUsedTypes: string[];
};

function CallFileIcon({ callFile }: { callFile: CallFile }) {
  const fileType = resolveCallFileType(callFile);
  if (fileType === CallFileType.AUDIO) return <IconHeadphones />;
  if (fileType === CallFileType.VIDEO) return <IconVideo />;
  return <IconDocument />;
}

export default function Calls(props: Props) {
  const T = useT();
  const {
    params,
    files,
    totalCount,
    totalSize,
    totalDuration,
    currentPage,
    onPageChange,
    isLoading = false,
    onFileRemove,
    onFileRename,
    onTypeChange,
    onStatusUpdate,
    members,
    onMemberChange,
    onAddMember,
    memberFilter,
    onMemberFilterChange,
    onSelfAssign,
    typeFilter,
    onTypeFilterChange,
    statusFilter,
    onStatusFilterChange,
    period,
    customRange,
    onPeriodChange,
    projectUsedTypes,
  } = props;
  const { lang } = params;
  const user = useAuthUser();
  const showMember = useBrand() !== null;
  const isCompanyOwner = useIsCompanyOwner();
  const canManageCompany = useCanManageCompany();
  const canEditContent = useCanEditContent();
  const showAutoRun = !showMember || isCompanyOwner;
  // Managers manage everything; the member attribution is manager-only (auto-set to the uploader).
  const canManage = !showMember || canManageCompany;
  // A call's own fields (name, type) are editable by a manager or the member who uploaded it.
  const canEditCall = (callFile: CallFile) =>
    canManage || callFile.savedCall?.uploaderId === user?.id;

  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  const project = ProjectStore((state) => state.currentProject);
  const [autoRunRoles, setAutoRunRoles] = useState<ProjectMemberRole[]>([]);

  useEffect(() => {
    setAutoRunRoles(project?.callAnalysisAutoRunRoles ?? []);
  }, [project?.callAnalysisAutoRunRoles]);

  const persistAutoRunRoles = (roles: ProjectMemberRole[]) => {
    const current = ProjectStore.getState().currentProject;
    if (!current) {
      return;
    }
    setAutoRunRoles(roles);
    ProjectStore.getState().updateProject({
      ...current,
      callAnalysisAutoRunRoles: roles,
    });
    setProjectCallAnalysisAutoRunRoles(current.id, roles);
  };

  const handleAutoRunToggle = (checked: boolean) => {
    if (checked) {
      ModalStore.getState().openModal(
        Components.CallAnalysisAutoRunConfirm,
        T(TKey.CALL_ANALYSIS_AUTO_RUN_MODAL_TITLE),
        {
          params,
          roles: showMember ? autoRunRoles : [ProjectMemberRole.OWNER],
          onConfirm: (roles: ProjectMemberRole[]) => persistAutoRunRoles(roles),
          cumulativeSeconds: project?.callsBilledSeconds,
          withRoles: showMember,
        },
        ModalType.MEDIUM,
      );
    } else {
      persistAutoRunRoles([]);
    }
  };

  useEffect(() => {
    if (scrollWrapperRef.current && files.length > 0) {
      scrollWrapperRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [files.length]);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const paginationData: PaginationData = {
    page: currentPage,
    totalPages,
    hasNext: currentPage < totalPages,
    hasPrevious: currentPage > 1,
    limit: 0,
  };

  const confirmDeleteCall = (callFile: CallFile, overlay: boolean, onDeleted?: () => void) => {
    const analyzed = callFile.status === CallStatus.ANALYZED;
    ModalStore.getState().openConfirmDialog(
      params,
      TKey.CALL_DELETE_TITLE,
      analyzed ? TKey.CALL_DELETE_TEXT_ANALYZED : TKey.CALL_DELETE_TEXT,
      TKey.CALL_DELETE_CONFIRM,
      () => {
        onFileRemove(callFile.id);
        onDeleted?.();
      },
      overlay,
    );
  };

  const openCallDetail = (callFile: CallFile) => {
    // Deleting from inside the detail opens the confirm as an overlay, then closes the detail.
    const removeFromDetail = () =>
      confirmDeleteCall(callFile, true, () => ModalStore.getState().closeModal());
    ModalStore.getState().openModal(
      Components.CallDetail,
      <CallTitle
        name={getCallDisplayName(callFile)}
        onSave={(label) => onFileRename(callFile.id, label)}
        large={true}
        showEditBtn={true}
        beforeIcon={<CallFileIcon callFile={callFile} />}
      />,
      {
        params,
        callFile,
        onRemove: removeFromDetail,
        onTypeChange: (type: string | null) => onTypeChange(callFile.id, type),
        members,
        onMemberChange: (memberId: number | null) => onMemberChange(callFile.id, memberId),
        onAddMember: () => onAddMember(callFile.id),
        onSelfAssign: () => onSelfAssign(callFile.id),
        currentPage,
        currentUserId: user?.id,
      },
      ModalType.LARGE,
      <CallDetailFooter
        params={params}
        callFile={callFile}
        onRemove={removeFromDetail}
        onStatusChange={(status) => onStatusUpdate(callFile.id, status)}
      />,
    );
  };

  const columns: { key: CallColumn; label: string }[] = [
    { key: CallColumn.NAME, label: T(TKey.CALL_ANALYSIS_COL_NAME) as string },
    { key: CallColumn.TYPE, label: T(TKey.CALL_TYPE_LABEL) as string },
    ...(showMember
      ? [
          {
            key: CallColumn.MEMBER,
            label: T(TKeyProjectMember.PROJECT_MEMBER_COL) as string,
          },
        ]
      : []),
    {
      key: CallColumn.STATUS,
      label: T(TKey.CALL_ANALYSIS_COL_STATUS) as string,
    },
    { key: CallColumn.RUN, label: "" },
    { key: CallColumn.MENU, label: "" },
  ];

  const placeholderRows = showMember
    ? CALLS_PLACEHOLDER_ROWS
    : CALLS_PLACEHOLDER_ROWS.map((row) => ({
        ...row,
        cells: row.cells.filter((_, i) => i !== 2),
      }));

  return (
    <div className={`${styles.container} ${!showMember ? styles.noMember : ""}`}>
      <div className={styles.header}>
        <h2 className={styles.title}>{T(TKey.CALL_ANALYSIS_LIBRARY_TITLE)}</h2>
        <div className={styles.headerActions}>
          {user && <PeriodFilter period={period} custom={customRange} onChange={onPeriodChange} />}
          {user && showAutoRun && (
            <ToggleInput
              checked={autoRunRoles.length > 0}
              onToggle={handleAutoRunToggle}
              label={T(TKey.CALL_ANALYSIS_AUTO_RUN) as string}
              className={styles.autoRunToggle}
            />
          )}
        </div>
      </div>

      {!user ? (
        <div className={styles.scrollWrapper}>
          <div className={styles.tableInner}>
            <div className={styles.tableHeader}>
              {columns.map((col) => (
                <span key={col.key}>{col.label}</span>
              ))}
            </div>
            <BlurredPlaceholder
              rows={placeholderRows}
              className={styles.list}
              rowClassName={styles.item}
              gridColumns="var(--calls-grid-cols)"
            />
          </div>
        </div>
      ) : isLoading && files.length === 0 ? (
        <div className={styles.loaderContainer}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.CALL_ANALYSIS_LOADER_DELAY} />
        </div>
      ) : files.length === 0 &&
        memberFilter === undefined &&
        typeFilter === undefined &&
        statusFilter === undefined &&
        period === CallPeriod.ALL ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <IconHeadphones />
          </div>
          <div className={styles.emptyTexts}>
            <p className={styles.emptyTitle}>{T(TKey.CALL_ANALYSIS_LIBRARY_EMPTY)}</p>
            <p className={styles.emptySubtitle}>
              {T(
                canEditContent
                  ? TKey.CALL_ANALYSIS_LIBRARY_EMPTY_SUB
                  : TKey.CALL_ANALYSIS_LIBRARY_EMPTY_SUB_READER,
              )}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.scrollWrapper} ref={scrollWrapperRef}>
            <div className={styles.tableInner}>
              <div className={styles.tableHeader}>
                {columns.map((col) => (
                  <span key={col.key}>
                    {col.label}
                    {col.key === CallColumn.STATUS && (
                      <StatusFilter
                        value={statusFilter}
                        onChange={onStatusFilterChange}
                        lang={lang}
                      />
                    )}
                    {col.key === CallColumn.MEMBER && members.length > 0 && (
                      <MemberFilter
                        members={members}
                        value={memberFilter}
                        onChange={onMemberFilterChange}
                        lang={lang}
                        currentUserId={user?.id}
                      />
                    )}
                    {col.key === CallColumn.TYPE && (
                      <TypeFilter
                        types={projectUsedTypes}
                        value={typeFilter}
                        onChange={onTypeFilterChange}
                        lang={lang}
                      />
                    )}
                  </span>
                ))}
              </div>

              <ul className={styles.list}>
                {isLoading ? (
                  <li className={styles.loaderRow}>
                    <PointsLoader
                      color="var(--color-green-3)"
                      delay={Limits.CALL_ANALYSIS_LOADER_DELAY}
                    />
                  </li>
                ) : files.length === 0 ? (
                  <li className={styles.emptyFilter}>{T(TKey.CALL_ANALYSIS_LIBRARY_NO_RESULTS)}</li>
                ) : null}
                {!isLoading &&
                  files.map((callFile) => (
                    <li
                      key={callFile.id}
                      className={`${styles.item} ${
                        callFile.savedCall?.excludedFromAnalysis ? styles.itemExcluded : ""
                      }`}
                      onClick={() => openCallDetail(callFile)}
                    >
                      <div className={styles.itemName}>
                        <span className={styles.itemIcon}>
                          <CallFileIcon callFile={callFile} />
                        </span>
                        <CallTitle
                          name={getCallDisplayName(callFile)}
                          onSave={(label) => onFileRename(callFile.id, label)}
                          disabled={!canEditCall(callFile)}
                        />
                      </div>

                      <div className={styles.itemType} onClick={(e) => e.stopPropagation()}>
                        <TypeSelector
                          value={callFile.savedCall?.type || null}
                          onChange={(type) => onTypeChange(callFile.id, type)}
                          params={params}
                          disabled={
                            callFile.status !== CallStatus.UPLOADED || !canEditCall(callFile)
                          }
                          suggestions={projectUsedTypes}
                        />
                      </div>

                      {showMember && (
                        <div className={styles.itemMember} onClick={(e) => e.stopPropagation()}>
                          {callFile.savedCall &&
                            (canManage ? (
                              <MemberSelector
                                value={callFile.savedCall.member?.id ?? null}
                                members={members}
                                onChange={(memberId) => onMemberChange(callFile.id, memberId)}
                                onAddClick={() => onAddMember(callFile.id)}
                                onAssignMe={() => onSelfAssign(callFile.id)}
                                params={params}
                                currentUserId={user?.id}
                              />
                            ) : (
                              <Badge className={styles.readOnlyMember}>
                                {callFile.savedCall.member?.name ?? "-"}
                              </Badge>
                            ))}
                        </div>
                      )}

                      <CallStatusBadge status={callFile.status} lang={lang} />

                      <div className={styles.playBtnContainer} onClick={(e) => e.stopPropagation()}>
                        {(!callFile.status || callFile.status === CallStatus.ANALYZING) && (
                          <CircleLoader size={20} color="var(--color-green-3)" />
                        )}
                        <CallInsightToggle params={params} callFile={callFile} />
                        <RunAnalysis
                          params={params}
                          callId={callFile.id}
                          onStart={(newStatus) => onStatusUpdate(callFile.id, newStatus)}
                          mode={RunAnalysisMode.ICON}
                          callFile={callFile}
                        />
                      </div>

                      <div className={styles.menuBtnContainer} onClick={(e) => e.stopPropagation()}>
                        <CallRowMenu
                          params={params}
                          callFile={callFile}
                          onOpen={() => openCallDetail(callFile)}
                          onRemove={() => confirmDeleteCall(callFile, false)}
                          onStatusUpdate={(newStatus) => onStatusUpdate(callFile.id, newStatus)}
                        />
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className={styles.footer}>
            <span className={styles.footerCount}>
              {totalCount} {T(TKey.CALL_ANALYSIS_LIBRARY_TOTAL)}
              {totalCount > 1 ? "s" : ""}
              {" · "}
              {UTime.formatSeconds(totalDuration)}
              {" · "}
              {UFile.formatFileSize(totalSize)}
            </span>

            <Pagination data={paginationData} onPageChange={onPageChange} maxVisiblePages={5} />
          </div>
        </>
      )}
    </div>
  );
}
